"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { motion, AnimatePresence } from "framer-motion"
import { Plus, Edit, Trash2, Save, X, Building2, ImageIcon, Eye, EyeOff, UserCheck } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { getAllStartups } from "../utils/api"

const AdminInvestors = () => {
  const [investors, setInvestors] = useState([])
  const [showForm, setShowForm] = useState(false)
  const MIN_LIMIT = 100000;
  const MAX_LIMIT = 10000000;
  const STEP = 50000;
  const DEFAULT_RANGE = {
    minAmount: 100000,
    maxAmount: 1000000,
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    number: "",
    image: "",
    linkedinProfile: "",
    investmentRange: DEFAULT_RANGE, // ✅ NEVER undefined
  });

  const [imagePreview, setImagePreview] = useState("");
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const [editingId, setEditingId] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [toggle, setToggle] = useState(false)
  const [loading, setLoading] = useState(false)

  // NEW: track which investor is being processed for active toggle
  const [processingId, setProcessingId] = useState(null)

  // Fetch investors
  const fetchInvestors = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/ourinvestors`)
      setInvestors(res.data)
    } catch (error) {
      console.error("Error fetching investors:", error)
    }
  }

  // Fetch toggle state
  const fetchToggleState = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/investortoggle`)
      setToggle(res.data.enabled)
    } catch (error) {
      console.error("Error fetching toggle state:", error)
    }
  }

  const handleToggleChange = async () => {
    const updated = !toggle
    setToggle(updated)
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/investortoggle`, { enabled: updated })
    } catch (error) {
      console.error("Error updating toggle:", error)
    }
  }

  useEffect(() => {
    fetchInvestors()
    fetchToggleState()
  }, [])

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      investmentRange: prev.investmentRange ?? DEFAULT_RANGE, // 🛡️ GUARANTEE
    }));
  };

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    })

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      if (editingId) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/ourinvestors/${editingId}`, formData)
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/ourinvestors`, formData)
      }
      setFormData({
        ...formData, name: "",
        email: "",
        number: "",
        image: "",
        linkedinProfile: "",
        investmentRange: DEFAULT_RANGE
      })
      setShowForm(false)
      setEditingId(null)
      fetchInvestors()

    } catch (error) {
      console.error("Error submitting form:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (investor) => {
    setFormData(investor)
    setEditingId(investor._id)
    setShowForm(true)
  }

  const handleDeleteConfirm = (id) => {
    setShowDeleteConfirm(true)
    setDeleteId(id)
  }

  const handleDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/ourinvestors/${deleteId}`)
      setShowDeleteConfirm(false)
      setDeleteId(null)
      fetchInvestors()
    } catch (error) {
      console.error("Error deleting investor:", error)
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case "Our Key Partner":
        return "bg-purple-100 text-purple-800 border-purple-200"
      case "Our Partner":
        return "bg-blue-100 text-blue-800 border-blue-200"
      case "Our Investor":
        return "bg-green-100 text-green-800 border-green-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  const openNewForm = () => {
    setFormData({ type: "", name: "", image: "" })
    setEditingId(null)
    setShowForm(true)
  }

  // UPDATED: toggle active/inactive for a given investor
  const handleActiveToggle = async (investorId, currentState) => {
    const newState = !currentState
    setProcessingId(investorId)

    // optimistic update: flip state locally immediately so UI feels snappy
    setInvestors((prev) =>
      prev.map((inv) => (inv._id === investorId ? { ...inv, isActive: newState } : inv))
    )

    try {
      // first try dedicated endpoints (if your backend has them)
      if (newState) {
        // activate
        try {
          await axios.patch(`${process.env.REACT_APP_API_URL}/api/ourinvestors/${investorId}/approve`)
        } catch (errApprove) {
          // fallback to generic update: send full object with updated isActive
          const inv = investors.find((i) => i._id === investorId) || {}
          await axios.put(`${process.env.REACT_APP_API_URL}/api/ourinvestors/${investorId}`, {
            ...inv,
            isActive: true,
          })
        }
      } else {
        // deactivate
        try {
          await axios.patch(`${process.env.REACT_APP_API_URL}/api/ourinvestors/${investorId}/deactivate`)
        } catch (errDeactivate) {
          const inv = investors.find((i) => i._id === investorId) || {}
          await axios.put(`${process.env.REACT_APP_API_URL}/api/ourinvestors/${investorId}`, {
            ...inv,
            isActive: false,
          })
        }
      }

      // refresh list to ensure server-authoritative data
      await fetchInvestors()
    } catch (error) {
      console.error("Error toggling active state:", error)
      // rollback optimistic update on error
      setInvestors((prev) =>
        prev.map((inv) => (inv._id === investorId ? { ...inv, isActive: currentState } : inv))
      )
    } finally {
      setProcessingId(null)
    }
  }

  // const { data: startups, isLoading, refetch } = useQuery({
  //   queryKey: ['startups'],
  //   queryFn: getAllStartups
  // })

  // useEffect(() => {
  //   console.log(startups)
  // }, [startups])

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Investor Management</h1>
                <p className="text-gray-500">Manage investors, partners, and key stakeholders</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Toggle Switch */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Display Section</span>
                <button
                  onClick={handleToggleChange}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${toggle ? "bg-blue-600" : "bg-gray-300"
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${toggle ? "translate-x-6" : "translate-x-1"
                      }`}
                  />
                </button>
                {toggle ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
              </div>

              <button
                onClick={openNewForm}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Investor
              </button>
            </div>
          </div>
        </div>

        {/* Status Banner */}
        <div
          className={`mb-8 p-4 rounded-xl border ${toggle ? "bg-green-50 border-green-200" : "bg-orange-50 border-orange-200"
            }`}
        >
          <div className="flex items-center gap-3">
            {toggle ? <UserCheck className="w-5 h-5 text-green-600" /> : <EyeOff className="w-5 h-5 text-orange-600" />}
            <div>
              <p className={`font-medium ${toggle ? "text-green-800" : "text-orange-800"}`}>
                Investor Section is {toggle ? "Enabled" : "Disabled"}
              </p>
              <p className={`text-sm ${toggle ? "text-green-600" : "text-orange-600"}`}>
                {toggle
                  ? "Investors are visible on the public website"
                  : "Investors are hidden from the public website"}
              </p>
            </div>
          </div>
        </div>

        {/* Investors Grid */}
        {toggle ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {investors.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center py-16">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                    <Building2 className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Investors Found</h3>
                  <p className="text-gray-500 mb-6">Get started by adding your first investor or partner</p>
                  <button
                    onClick={openNewForm}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add First Investor
                  </button>
                </div>
              ) : (
                investors.map((investor, index) => (
                  <motion.div
                    key={investor._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-gray-200 flex-shrink-0">
                        {investor.image ? (
                          <img
                            src={investor.image || "/placeholder.svg"}
                            alt={investor.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = "none"
                              e.target.nextSibling.style.display = "flex"
                            }}
                          />
                        ) : null}
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{investor.name}</h3>
                        <div className="flex items-center gap-3 mt-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(investor.type)}`}
                          >
                            Investor
                          </span>

                          {/* Active / Inactive status badge */}
                          <button
                            onClick={() => handleActiveToggle(investor._id, !!investor.isActive)}
                            disabled={processingId === investor._id}
                            className={`inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-xs font-medium border ${investor.isActive
                              ? "bg-green-50 text-green-800 border-green-200"
                              : "bg-gray-50 text-gray-600 border-gray-200"
                              } transition-colors`}
                            title={investor.isActive ? "Click to deactivate" : "Click to activate"}
                          >
                            {processingId === investor._id ? (
                              <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"></path>
                              </svg>
                            ) : investor.isActive ? (
                              <UserCheck className="w-3 h-3 text-green-600" />
                            ) : (
                              <EyeOff className="w-3 h-3 text-gray-500" />
                            )}
                            <span>{investor.isActive ? "Active" : "Inactive"}</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(investor)}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Edit className="w-3 h-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteConfirm(investor._id)}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <EyeOff className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Investor Section Disabled</h3>
            <p className="text-gray-500 mb-6">Enable the investor section to view and manage investors</p>
            <button
              onClick={handleToggleChange}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
            >
              <Eye className="w-4 h-4" />
              Enable Section
            </button>
          </div>
        )}
      </div>

      {/* Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingId ? "Edit Investor" : "Add New Investor"}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleFormSubmit} className="p-6 space-y-6">

                {/* 2 Column Grid */}
                <div className="grid grid-cols-2 gap-6">

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Investor Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        // Optional size check (VERY IMPORTANT)
                        if (file.size > 300 * 1024) {
                          alert("Image must be less than 300KB");
                          return;
                        }

                        const base64 = await convertToBase64(file);

                        setFormData((prev) => ({
                          ...prev,
                          image: base64, // ✅ STRING, NOT OBJECT
                        }));
                      }}
                    />
                  </div>
                  <div className="flex items-end">
                    {(imagePreview || typeof formData.image === "string") && (
                      <img
                        src={imagePreview || formData.image}
                        alt="Preview"
                        className="w-24 h-24 rounded-full object-cover border shadow-sm"
                      />
                    )}
                  </div>
                  {/* Name */}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border rounded-lg"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border rounded-lg"
                      required
                    />
                  </div>

                  {/* Mobile */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      name="number"
                      value={formData.number}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border rounded-lg"
                      required
                    />
                  </div>

                  {/* LinkedIn */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedinProfile"
                      value={formData.linkedinProfile}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border rounded-lg"
                    />
                  </div>





                  {/* Investment Range Slider */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Investment Range
                    </label>

                    {/* Min slider */}
                    <input
                      type="range"
                      min={MIN_LIMIT}
                      max={MAX_LIMIT}
                      step={STEP}
                      value={formData.investmentRange?.minAmount ?? DEFAULT_RANGE.minAmount}
                      onChange={(e) => {
                        const value = Number(e.target.value);

                        setFormData((prev) => ({
                          ...prev,
                          investmentRange: {
                            minAmount: value,
                            maxAmount: Math.max(
                              value,
                              prev.investmentRange?.maxAmount ?? DEFAULT_RANGE.maxAmount
                            ),
                          },
                        }));
                      }}
                      className="w-full accent-blue-600"
                    />

                    {/* Max slider */}
                    <input
                      type="range"
                      min={MIN_LIMIT}
                      max={MAX_LIMIT}
                      step={STEP}
                      value={formData.investmentRange?.maxAmount ?? DEFAULT_RANGE.maxAmount}
                      onChange={(e) => {
                        const value = Number(e.target.value);

                        setFormData((prev) => ({
                          ...prev,
                          investmentRange: {
                            minAmount: Math.min(
                              value,
                              prev.investmentRange?.minAmount ?? DEFAULT_RANGE.minAmount
                            ),
                            maxAmount: value,
                          },
                        }));
                      }}
                      className="w-full accent-blue-600 mt-3"
                    />

                    <div className="flex justify-between text-sm mt-2 text-gray-600">
                      <span>
                        ₹{(formData.investmentRange?.minAmount ?? DEFAULT_RANGE.minAmount).toLocaleString()}
                      </span>
                      <span>
                        ₹{(formData.investmentRange?.maxAmount ?? DEFAULT_RANGE.maxAmount).toLocaleString()}
                      </span>
                    </div>
                  </div>



                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-4 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-3 bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg"
                  >
                    {loading
                      ? "Saving..."
                      : editingId
                        ? "Update Investor"
                        : "Create Investor"}
                  </button>
                </div>

              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to delete this investor? This will permanently remove them from the system.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AdminInvestors;